// client/src/dnd/views/Interactive_parts/DraggableImageOverlay.js
import React, { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

function DraggableImageOverlay({ url, initialBounds, pane = "overlay-pane-high", zIndex = 9900, editing = true, onChange }) {
  const map = useMap();
  const layerRef = useRef(null);
  const dragStateRef = useRef(null);
  const mouseMoveHandlerRef = useRef(null);
  const mouseUpHandlerRef = useRef(null);

  useEffect(() => {
    if (!map || !url || !initialBounds) return;

    const layer = L.imageOverlay(url, initialBounds, {
      pane,
      interactive: true,
    });

    if (Number.isFinite(zIndex) && layer.setZIndex) {
      layer.setZIndex(zIndex);
    }

    layer.addTo(map);
    layerRef.current = layer;

    const commitChange = () => {
      if (!onChange || !layerRef.current) return;
      const b = layerRef.current.getBounds();
      const sw = b.getSouthWest();
      const ne = b.getNorthEast();
      const nb = [
        [sw.lat, sw.lng],
        [ne.lat, ne.lng],
      ];
      let rot = null;
      try {
        if (layerRef.current.pm && typeof layerRef.current.pm.getRotation === "function") {
          rot = layerRef.current.pm.getRotation();
        } else if (typeof layerRef.current.options.rotation === "number") {
          rot = layerRef.current.options.rotation;
        } else if (typeof layerRef.current.options.rotationAngle === "number") {
          rot = layerRef.current.options.rotationAngle;
        }
      } catch {}
      onChange(nb, rot);
    };

    const getRect = () => {
      const b = layer.getBounds();
      const sw = b.getSouthWest();
      const ne = b.getNorthEast();
      const pSW = map.latLngToContainerPoint(sw);
      const pNE = map.latLngToContainerPoint(ne);
      const left = Math.min(pSW.x, pNE.x);
      const right = Math.max(pSW.x, pNE.x);
      const top = Math.min(pSW.y, pNE.y);
      const bottom = Math.max(pSW.y, pNE.y);
      return { left, right, top, bottom };
    };

    const getModeForPoint = (pt) => {
      const margin = 10;
      const rect = getRect();
      const insideX = pt.x >= rect.left && pt.x <= rect.right;
      const insideY = pt.y >= rect.top && pt.y <= rect.bottom;
      if (!insideX || !insideY) return null;

      const nearLeft = Math.abs(pt.x - rect.left) <= margin;
      const nearRight = Math.abs(pt.x - rect.right) <= margin;
      const nearTop = Math.abs(pt.y - rect.top) <= margin;
      const nearBottom = Math.abs(pt.y - rect.bottom) <= margin;

      if (nearLeft && nearTop) return "resize-nw";
      if (nearRight && nearTop) return "resize-ne";
      if (nearLeft && nearBottom) return "resize-sw";
      if (nearRight && nearBottom) return "resize-se";
      if (nearLeft) return "resize-w";
      if (nearRight) return "resize-e";
      if (nearTop) return "resize-n";
      if (nearBottom) return "resize-s";
      return "move";
    };

    const applyRectToLayer = (rect) => {
      const minSize = 5;
      const width = rect.right - rect.left;
      const height = rect.bottom - rect.top;
      if (width < minSize || height < minSize) return;
      const pSW = L.point(rect.left, rect.bottom);
      const pNE = L.point(rect.right, rect.top);
      const llSW = map.containerPointToLatLng(pSW);
      const llNE = map.containerPointToLatLng(pNE);
      const nb = L.latLngBounds(llSW, llNE);
      layer.setBounds(nb);
    };

    const stopDragging = () => {
      if (dragStateRef.current) {
        dragStateRef.current = null;
        if (map.dragging && map.dragging.enable) {
          map.dragging.enable();
        }
        if (mouseMoveHandlerRef.current) {
          document.removeEventListener("mousemove", mouseMoveHandlerRef.current);
        }
        if (mouseUpHandlerRef.current) {
          document.removeEventListener("mouseup", mouseUpHandlerRef.current);
        }
        commitChange();
      }
    };

    const handleMouseMove = (ev) => {
      if (!dragStateRef.current) return;
      const ds = dragStateRef.current;
      const pt = map.mouseEventToContainerPoint(ev);
      const dx = pt.x - ds.startPoint.x;
      const dy = pt.y - ds.startPoint.y;
      const rect = { ...ds.startRect };
      const minSize = 20;

      switch (ds.mode) {
        case "move":
          rect.left = ds.startRect.left + dx;
          rect.right = ds.startRect.right + dx;
          rect.top = ds.startRect.top + dy;
          rect.bottom = ds.startRect.bottom + dy;
          break;
        case "resize-w": {
          let nl = ds.startRect.left + dx;
          if (ds.startRect.right - nl < minSize) nl = ds.startRect.right - minSize;
          rect.left = nl;
          break;
        }
        case "resize-e": {
          let nr = ds.startRect.right + dx;
          if (nr - ds.startRect.left < minSize) nr = ds.startRect.left + minSize;
          rect.right = nr;
          break;
        }
        case "resize-n": {
          let nt = ds.startRect.top + dy;
          if (ds.startRect.bottom - nt < minSize) nt = ds.startRect.bottom - minSize;
          rect.top = nt;
          break;
        }
        case "resize-s": {
          let nb = ds.startRect.bottom + dy;
          if (nb - ds.startRect.top < minSize) nb = ds.startRect.top + minSize;
          rect.bottom = nb;
          break;
        }
        case "resize-nw": {
          let nl = ds.startRect.left + dx;
          let nt = ds.startRect.top + dy;
          if (ds.startRect.right - nl < minSize) nl = ds.startRect.right - minSize;
          if (ds.startRect.bottom - nt < minSize) nt = ds.startRect.bottom - minSize;
          rect.left = nl;
          rect.top = nt;
          break;
        }
        case "resize-ne": {
          let nr = ds.startRect.right + dx;
          let nt = ds.startRect.top + dy;
          if (nr - ds.startRect.left < minSize) nr = ds.startRect.left + minSize;
          if (ds.startRect.bottom - nt < minSize) nt = ds.startRect.bottom - minSize;
          rect.right = nr;
          rect.top = nt;
          break;
        }
        case "resize-sw": {
          let nl = ds.startRect.left + dx;
          let nb = ds.startRect.bottom + dy;
          if (ds.startRect.right - nl < minSize) nl = ds.startRect.right - minSize;
          if (nb - ds.startRect.top < minSize) nb = ds.startRect.top + minSize;
          rect.left = nl;
          rect.bottom = nb;
          break;
        }
        case "resize-se": {
          let nr = ds.startRect.right + dx;
          let nb = ds.startRect.bottom + dy;
          if (nr - ds.startRect.left < minSize) nr = ds.startRect.left + minSize;
          if (nb - ds.startRect.top < minSize) nb = ds.startRect.top + minSize;
          rect.right = nr;
          rect.bottom = nb;
          break;
        }
        default:
          break;
      }

      applyRectToLayer(rect);
    };

    const handleMouseUp = () => {
      stopDragging();
    };

    mouseMoveHandlerRef.current = handleMouseMove;
    mouseUpHandlerRef.current = handleMouseUp;

    const handleMouseDown = (e) => {
      if (!editing) return;
      if (e.originalEvent && e.originalEvent.button !== 0) return;
      const ev = e.originalEvent || e;
      ev.preventDefault();
      ev.stopPropagation();

      const pt = map.mouseEventToContainerPoint(ev);
      const mode = getModeForPoint(pt);
      if (!mode) return;

      const rect = getRect();
      dragStateRef.current = {
        mode,
        startPoint: pt,
        startRect: rect,
      };

      if (map.dragging && map.dragging.disable) {
        map.dragging.disable();
      }

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    };

    const enableRotationOnly = () => {
      if (!editing) return;
      if (layer.pm && layer.pm.enable) {
        try {
          layer.pm.enable({
            draggable: false,
            snappable: false,
            allowEditing: false,
            allowRotation: true,
          });
        } catch {}
      }
    };

    const pmRotateEnd = () => {
      commitChange();
    };

    const el = layer.getElement();
    if (el && editing) {
      layer.on("mousedown", handleMouseDown);
    }

    if (editing) {
      enableRotationOnly();
      layer.on("pm:rotateend", pmRotateEnd);
    }

    return () => {
      if (editing) {
        layer.off("pm:rotateend", pmRotateEnd);
        layer.off("mousedown", handleMouseDown);
      }
      stopDragging();
      if (map && map.removeLayer) {
        map.removeLayer(layer);
      }
      layerRef.current = null;
    };
  }, [map, url, pane, zIndex, editing, JSON.stringify(initialBounds), onChange]);

  return null;
}

export default DraggableImageOverlay;
